import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Heading1, Heading2, Heading3 } from 'lucide-react';
import type { Outline } from '@seo/types';
import { fadeUp, stagger } from '@/components/motion';
import { cn } from '@/lib/cn';

export interface OutlineTreeProps {
  outline: Outline;
  /** Open all H2 sections by default. */
  defaultAllOpen?: boolean;
  className?: string;
}

/**
 * Nested motion accordion that renders a brief outline as H1 → H2 → H3.
 */
export function OutlineTree({
  outline,
  defaultAllOpen = false,
  className,
}: OutlineTreeProps): JSX.Element {
  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-gradient-to-br from-primary/10 via-card/50 to-card/30 p-4 backdrop-blur">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 ring-1 ring-primary/30">
          <Heading1 className="h-4 w-4 text-primary" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            H1 / Page title
          </div>
          <h2 className="text-lg font-semibold leading-tight text-foreground">
            {outline.h1}
          </h2>
        </div>
      </div>

      <motion.ul
        variants={stagger}
        initial="hidden"
        animate="show"
        className="space-y-2"
      >
        {outline.sections.map((section, idx) => (
          <OutlineSection
            key={`${section.h2}-${idx}`}
            index={idx + 1}
            h2={section.h2}
            h3s={section.h3s}
            summary={section.summary}
            defaultOpen={defaultAllOpen}
          />
        ))}
      </motion.ul>
    </div>
  );
}

interface SectionProps {
  index: number;
  h2: string;
  h3s: string[];
  summary?: string;
  defaultOpen?: boolean;
}

function OutlineSection({
  index,
  h2,
  h3s,
  summary,
  defaultOpen = false,
}: SectionProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const hasBody = h3s.length > 0 || !!summary;
  return (
    <motion.li
      variants={fadeUp}
      layout
      className="overflow-hidden rounded-xl border border-border/60 bg-card/50 backdrop-blur"
    >
      <button
        type="button"
        onClick={() => hasBody && setOpen((o) => !o)}
        disabled={!hasBody}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/30 disabled:cursor-default disabled:hover:bg-transparent"
        aria-expanded={hasBody ? open : undefined}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background/70 font-mono text-xs text-muted-foreground ring-1 ring-border/60">
          {String(index).padStart(2, '0')}
        </span>
        <Heading2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
          {h2}
        </span>
        {hasBody ? (
          <ChevronRight
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-90',
            )}
          />
        ) : null}
      </button>
      {open && hasBody ? (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="border-t border-border/60 px-4 pb-4 pt-3"
        >
          {summary ? (
            <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
              {summary}
            </p>
          ) : null}
          {h3s.length > 0 ? (
            <ul className="space-y-1.5">
              {h3s.map((h, i) => (
                <li
                  key={`${h}-${i}`}
                  className="flex items-center gap-2 rounded-md bg-background/40 px-3 py-2 text-sm text-foreground/90 ring-1 ring-border/40"
                >
                  <Heading3 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{h}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </motion.div>
      ) : null}
    </motion.li>
  );
}
